import { createSignal, Show } from "solid-js";
import type { AdminControlPlaneSnapshot, SystemOperatorSession } from "../shared";
import { TargetProfilePicker } from "./TargetProfilePicker";

export function LoginPage(props: {
  controlPlane: AdminControlPlaneSnapshot | null;
  loading: boolean;
  error: string;
  onLogin: (email: string, password: string) => Promise<SystemOperatorSession>;
}) {
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [localError, setLocalError] = createSignal("");

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    setLocalError("");
    setSubmitting(true);
    try {
      await props.onLogin(email().trim(), password());
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center px-6">
      <div class="w-full max-w-md rounded-2xl border border-gray-800 bg-gray-900/90 shadow-2xl p-8">
        <div class="mb-8">
          <div class="flex items-center justify-between gap-3 mb-3">
            <div class="text-xs uppercase tracking-[0.2em] text-cyan-400">SearchGrid Control Plane</div>
            <TargetProfilePicker compact />
          </div>
          <h1 class="text-2xl font-bold text-white">System Operator Login</h1>
          <p class="mt-2 text-sm text-gray-400">
            Sign in with a system operator account before loading the admin dashboard.
          </p>
          <Show when={props.controlPlane}>
            {(snapshot) => (
              <div class="mt-4 flex items-center gap-2 text-xs text-gray-300">
                <span class="px-2 py-1 rounded-md border border-cyan-800 bg-cyan-950/40 text-cyan-300">
                  {snapshot().controlPlane.target}
                </span>
                <span class="px-2 py-1 rounded-md border border-gray-700 bg-gray-800">
                  {snapshot().controlPlane.mode}
                </span>
              </div>
            )}
          </Show>
        </div>

        <form class="space-y-4" onSubmit={submit}>
          <label class="block">
            <div class="text-sm font-medium text-gray-200 mb-2">Email</div>
            <input
              type="email"
              value={email()}
              onInput={(event) => setEmail(event.currentTarget.value)}
              autocomplete="username"
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-3 text-sm text-white outline-none focus:border-cyan-500"
              placeholder="system@example.com"
              required
            />
          </label>

          <label class="block">
            <div class="text-sm font-medium text-gray-200 mb-2">Password</div>
            <input
              type="password"
              value={password()}
              onInput={(event) => setPassword(event.currentTarget.value)}
              autocomplete="current-password"
              class="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-3 text-sm text-white outline-none focus:border-cyan-500"
              placeholder="Enter password"
              required
            />
          </label>

          <Show when={props.error || localError()}>
            <div class="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200">
              {localError() || props.error}
            </div>
          </Show>

          <button
            type="submit"
            disabled={props.loading || submitting()}
            class="w-full rounded-lg bg-cyan-500 px-4 py-3 text-sm font-semibold text-gray-950 transition hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {props.loading || submitting() ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
